const AUDIO_MEDIA_TYPE = 'audio';
const CLOSED_STATE = 'closed';
const GET_STATS = 'getstats';
const PEER_CONNECTION_CLOSE = 'close';
const PEER_CONNECTION_STATE_CHANGE = 'onconnectionstatechange';
const PEER_ICE_CONNECTION_STATE_CHANGE = 'oniceconnectionstatechange';
const STATE_EVENT_CATEGORIES = {
    onconnectionstatechange: 'peer-connection',
    ondtlsstatechange: 'dtls',
    oniceconnectionstatechange: 'ice-connection',
    onicegatheringstatechange: 'ice-gathering',
    onsignalingstatechange: 'signaling'
};
const NEGOTIATION_OPERATIONS = new Set([
    'addIceCandidate',
    'createAnswer',
    'createOffer',
    'setLocalDescription',
    'setRemoteDescription'
]);
const isRecord = (value) => Boolean(value)
    && typeof value === 'object'
    && !Array.isArray(value);
const optionalNumber = (value) => typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
const optionalString = (value) => typeof value === 'string' && value
    ? value
    : undefined;
const optionalBoolean = (value) => typeof value === 'boolean'
    ? value
    : undefined;
const omitUndefined = (value) => {
    Object.keys(value).forEach(key => {
        if (value[key] === undefined) {
            delete value[key];
        }
    });
    return value;
};
const isAudioReport = (report) => report.kind === AUDIO_MEDIA_TYPE
    || report.mediaType === AUDIO_MEDIA_TYPE;
const addCounter = (current, next) => {
    const number = optionalNumber(next);
    if (number === undefined) {
        return current;
    }
    return (current ?? 0) + number;
};
/**
 * Builds the track descriptor shared with the embedding application.
 *
 * @param {IParticipant} participant - The Jitsi participant.
 * @param {ITrack|undefined} audioTrack - The participant's audio track, if present.
 * @returns {IAudioTrackDescriptor}
 */
function buildTrackDescriptor(participant, audioTrack) {
    const mediaStreamTrack = audioTrack?.jitsiTrack?.getTrack?.();
    const trackIdentifier = optionalString(mediaStreamTrack?.id);
    return omitUndefined({
        enabled: mediaStreamTrack?.enabled === true,
        local: participant.local === true,
        muted: audioTrack?.muted ?? true,
        participantId: participant.id,
        readyState: optionalString(mediaStreamTrack?.readyState) ?? 'missing',
        trackIdentifier,
        trackPresent: Boolean(audioTrack?.jitsiTrack && mediaStreamTrack)
    });
}
/**
 * Returns one audio-track descriptor for every real conference participant.
 *
 * @param {IReduxState} state - Current Redux state.
 * @returns {IAudioTrackDescriptor[]}
 */
function getAudioTrackDescriptors(state) {
    const participantsState = state['features/base/participants'];
    const tracks = state['features/base/tracks'];
    const participants = [
        ...participantsState.local ? [participantsState.local] : [],
        ...participantsState.remote.values()
    ];
    return participants.map(participant => {
        const audioTrack = tracks.find(track => track.mediaType === AUDIO_MEDIA_TYPE
            && track.local === (participant.local === true)
            && (participant.local || track.participantId === participant.id));
        return buildTrackDescriptor(participant, audioTrack);
    });
}
/**
 * Adds cumulative counters from an RTP report to an existing counter object.
 *
 * @param {IAudioCounters} counters - Existing counters.
 * @param {RTCStatsReport} report - Reconstructed WebRTC report.
 * @returns {IAudioCounters}
 */
function addRTPCounters(counters, report) {
    return omitUndefined({
        bytesReceived: addCounter(counters.bytesReceived, report.bytesReceived),
        bytesSent: addCounter(counters.bytesSent, report.bytesSent),
        packetsReceived: addCounter(counters.packetsReceived, report.packetsReceived),
        packetsSent: addCounter(counters.packetsSent, report.packetsSent),
        totalAudioEnergy: addCounter(counters.totalAudioEnergy, report.totalAudioEnergy),
        totalSamples: addCounter(counters.totalSamples, report.totalSamplesReceived),
        totalSamplesDuration: addCounter(counters.totalSamplesDuration, report.totalSamplesDuration)
    });
}
/**
 * Correlates reconstructed audio reports with Jitsi participant tracks.
 *
 * @param {Map<string, RTCStatsReport>} reports - Reconstructed WebRTC reports.
 * @param {IReduxState} state - Current Redux state.
 * @param {number} timestamp - Snapshot timestamp.
 * @returns {IAudioStatsSnapshot}
 */
function buildAudioSnapshot(reports, state, timestamp) {
    const descriptors = getAudioTrackDescriptors(state);
    const inboundCounters = new Map();
    const outboundCounters = new Map();
    const sourceCounters = new Map();
    let playout;
    reports.forEach(report => {
        if (report.type === 'media-source' && isAudioReport(report)) {
            const trackIdentifier = optionalString(report.trackIdentifier);
            if (trackIdentifier) {
                sourceCounters.set(trackIdentifier, omitUndefined({
                    totalAudioEnergy: optionalNumber(report.totalAudioEnergy),
                    totalSamplesDuration: optionalNumber(report.totalSamplesDuration)
                }));
            }
            return;
        }
        if (report.type === 'media-playout' && (isAudioReport(report) || (!report.kind && !report.mediaType))) {
            playout = omitUndefined({
                totalSamples: addCounter(playout?.totalSamples, report.totalSamplesCount),
                totalSamplesDuration: addCounter(playout?.totalSamplesDuration, report.totalSamplesDuration)
            });
            return;
        }
        if (!isAudioReport(report)) {
            return;
        }
        if (report.type === 'inbound-rtp') {
            const trackId = optionalString(report.trackId);
            const trackReport = trackId ? reports.get(trackId) : undefined;
            const trackIdentifier = optionalString(report.trackIdentifier)
                ?? optionalString(trackReport?.trackIdentifier);
            if (trackIdentifier) {
                inboundCounters.set(trackIdentifier, addRTPCounters(inboundCounters.get(trackIdentifier) ?? {}, report));
            }
            return;
        }
        if (report.type === 'outbound-rtp') {
            const mediaSourceId = optionalString(report.mediaSourceId);
            const mediaSource = mediaSourceId ? reports.get(mediaSourceId) : undefined;
            const trackId = optionalString(report.trackId);
            const trackReport = trackId ? reports.get(trackId) : undefined;
            const trackIdentifier = optionalString(mediaSource?.trackIdentifier)
                ?? optionalString(report.trackIdentifier)
                ?? optionalString(trackReport?.trackIdentifier);
            if (trackIdentifier) {
                outboundCounters.set(trackIdentifier, addRTPCounters(outboundCounters.get(trackIdentifier) ?? {}, report));
            }
        }
    });
    const tracks = descriptors.map(descriptor => {
        const trackIdentifier = descriptor.trackIdentifier;
        const rtpCounters = trackIdentifier
            ? descriptor.local
                ? outboundCounters.get(trackIdentifier)
                : inboundCounters.get(trackIdentifier)
            : undefined;
        const mediaSourceCounters = descriptor.local && trackIdentifier
            ? sourceCounters.get(trackIdentifier)
            : undefined;
        return {
            ...descriptor,
            ...rtpCounters,
            ...mediaSourceCounters
        };
    });
    return omitUndefined({
        playout,
        timestamp,
        tracks
    });
}
/**
 * Extracts the P2P marker from rtcstats' legacy PeerConnection constraints without forwarding the constraints.
 *
 * @param {unknown} data - Rtcstats constraints entry.
 * @returns {boolean|undefined}
 */
function getP2P(data) {
    if (!isRecord(data) || !Array.isArray(data.optional)) {
        return;
    }
    for (const option of data.optional) {
        if (isRecord(option) && typeof option.rtcStatsSFUP2P === 'boolean') {
            return option.rtcStatsSFUP2P;
        }
    }
}
/**
 * Reduces a raw rtcstats error to a stable error name. The raw error text is intentionally not forwarded.
 *
 * @param {unknown} data - Error-like rtcstats value.
 * @returns {string|undefined}
 */
function getErrorCode(data) {
    const raw = optionalString(data) ?? (isRecord(data) ? optionalString(data.name) : undefined);
    const code = raw?.split(':', 1)[0].trim();
    return code && /^[A-Za-z][A-Za-z0-9_.-]{0,63}$/.test(code) ? code : undefined;
}
/**
 * Converts a raw rtcstats PeerConnection callback into an allowlisted connectivity event.
 *
 * @param {string} statsType - Rtcstats entry type.
 * @param {unknown} data - Rtcstats entry body.
 * @param {ICachedRTCStats} cache - Per-PeerConnection state.
 * @returns {IConnectivityEvent|undefined}
 */
function buildConnectivityEvent(statsType, data, cache) {
    const stateCategory = STATE_EVENT_CATEGORIES[statsType];
    if (stateCategory) {
        const state = optionalString(data);
        if (!state) {
            return;
        }
        return omitUndefined({
            category: stateCategory,
            operation: 'state-change',
            p2p: cache.p2p,
            state
        });
    }
    if (statsType === 'ondtlserror') {
        return omitUndefined({
            category: 'dtls',
            errorCode: getErrorCode(data),
            operation: 'transport',
            p2p: cache.p2p,
            phase: 'failure'
        });
    }
    if (statsType === 'onnegotiationneeded') {
        return omitUndefined({
            category: 'negotiation',
            operation: 'negotiationneeded',
            p2p: cache.p2p,
            phase: 'start'
        });
    }
    if (statsType === PEER_CONNECTION_CLOSE) {
        return omitUndefined({
            category: 'peer-connection',
            operation: 'close',
            p2p: cache.p2p,
            state: CLOSED_STATE
        });
    }
    let operation = statsType;
    let phase = 'start';
    if (statsType.endsWith('OnSuccess')) {
        operation = statsType.slice(0, -'OnSuccess'.length);
        phase = 'success';
    }
    else if (statsType.endsWith('OnFailure')) {
        operation = statsType.slice(0, -'OnFailure'.length);
        phase = 'failure';
    }
    if (!NEGOTIATION_OPERATIONS.has(operation)) {
        return;
    }
    return omitUndefined({
        category: 'negotiation',
        errorCode: phase === 'failure' ? getErrorCode(data) : undefined,
        iceRestart: operation === 'createOffer' && phase === 'start' && isRecord(data)
            ? optionalBoolean(data.iceRestart)
            : undefined,
        operation,
        p2p: cache.p2p,
        phase
    });
}
/**
 * Extracts only categorical and health information for the selected ICE candidate pair.
 * Candidate addresses, ports, report IDs, SDP, and raw candidates never leave this function.
 *
 * @param {Map<string, RTCStatsReport>} reports - Reconstructed WebRTC reports.
 * @param {ICachedRTCStats} cache - Per-PeerConnection state.
 * @param {number} timestamp - Snapshot timestamp.
 * @returns {IConnectivityStatsSnapshot|undefined}
 */
function buildConnectivitySnapshot(reports, cache, timestamp) {
    let selectedCandidatePairId;
    reports.forEach(report => {
        if (!selectedCandidatePairId && report.type === 'transport') {
            selectedCandidatePairId = optionalString(report.selectedCandidatePairId);
        }
    });
    if (!selectedCandidatePairId) {
        reports.forEach((report, reportId) => {
            if (!selectedCandidatePairId
                && report.type === 'candidate-pair'
                && (report.selected === true || (report.nominated === true && report.state === 'succeeded'))) {
                selectedCandidatePairId = reportId;
            }
        });
    }
    if (!selectedCandidatePairId) {
        return;
    }
    const pair = reports.get(selectedCandidatePairId);
    if (!pair) {
        return;
    }
    const localCandidateId = optionalString(pair.localCandidateId);
    const remoteCandidateId = optionalString(pair.remoteCandidateId);
    const localCandidate = localCandidateId ? reports.get(localCandidateId) : undefined;
    const remoteCandidate = remoteCandidateId ? reports.get(remoteCandidateId) : undefined;
    const routeChanged = cache.selectedCandidatePairId !== undefined
        && cache.selectedCandidatePairId !== selectedCandidatePairId;
    cache.selectedCandidatePairId = selectedCandidatePairId;
    return omitUndefined({
        availableIncomingBitrate: optionalNumber(pair.availableIncomingBitrate),
        availableOutgoingBitrate: optionalNumber(pair.availableOutgoingBitrate),
        candidateUrl: optionalString(localCandidate?.url),
        currentRoundTripTime: optionalNumber(pair.currentRoundTripTime),
        localCandidateType: optionalString(localCandidate?.candidateType),
        nominated: optionalBoolean(pair.nominated),
        p2p: cache.p2p,
        pairState: optionalString(pair.state),
        protocol: optionalString(localCandidate?.protocol),
        relayProtocol: optionalString(localCandidate?.relayProtocol),
        remoteCandidateType: optionalString(remoteCandidate?.candidateType),
        routeChanged,
        tcpType: optionalString(localCandidate?.tcpType),
        timestamp
    });
}
/**
 * Reconstructs delta-compressed rtcstats reports and emits allowlisted audio and connectivity telemetry.
 */
export class RTCStatsTelemetryCollector {
    _cache = new Map();
    _getState;
    constructor(getState) {
        this._getState = getState;
    }
    /**
     * Clears all PeerConnection report state when a conference ends.
     *
     * @returns {void}
     */
    clear() {
        this._cache.clear();
    }
    /**
     * Processes one rtcstats entry.
     *
     * @param {string} statsType - Rtcstats entry type.
     * @param {string|null} pcId - Rtcstats PeerConnection id.
     * @param {unknown} data - Rtcstats entry body.
     * @returns {IRTCStatsTelemetryEntry[]}
     */
    process(statsType, pcId, data) {
        if (!pcId) {
            return [];
        }
        const cache = this._cache.get(pcId) ?? {
            reports: new Map()
        };
        this._cache.set(pcId, cache);
        if (statsType === 'constraints') {
            cache.p2p = getP2P(data);
            return [{
                    data: omitUndefined({
                        category: 'peer-connection',
                        operation: 'create',
                        p2p: cache.p2p,
                        state: 'new'
                    }),
                    statsType: 'connectivity-event'
                }];
        }
        if (statsType === GET_STATS && isRecord(data)) {
            const reports = new Map();
            Object.entries(data).forEach(([reportId, delta]) => {
                if (reportId === 'timestamp' || !isRecord(delta)) {
                    return;
                }
                reports.set(reportId, {
                    ...cache.reports.get(reportId),
                    ...delta
                });
            });
            cache.reports = reports;
            const timestamp = optionalNumber(data.timestamp) ?? Date.now();
            const entries = [{
                    data: buildAudioSnapshot(reports, this._getState(), timestamp),
                    statsType: 'audio-stats'
                }];
            const connectivity = buildConnectivitySnapshot(reports, cache, timestamp);
            if (connectivity) {
                entries.push({
                    data: connectivity,
                    statsType: 'connectivity-stats'
                });
            }
            return entries;
        }
        const connectivityEvent = buildConnectivityEvent(statsType, data, cache);
        const shouldClear = statsType === PEER_CONNECTION_CLOSE
            || ((statsType === PEER_CONNECTION_STATE_CHANGE || statsType === PEER_ICE_CONNECTION_STATE_CHANGE)
                && data === CLOSED_STATE);
        if (shouldClear) {
            this._cache.delete(pcId);
        }
        return connectivityEvent ? [{
                data: connectivityEvent,
                statsType: 'connectivity-event'
            }] : [];
    }
}
